import { Component, ViewChild, ChangeDetectorRef, ElementRef, OnDestroy, AfterViewInit, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { IndSectionHeaderViewDataModel } from '@omni/models/indSectionHeaderDataModel';
import { ModalController, NavParams } from '@ionic/angular';
import { SignaturePad } from '@omni/components/shared/signaturepad/angular2-signaturepad.component';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@omni/services/alert/alert.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DatePipe } from '@angular/common';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface MedicalInquiryDualSignatureResult {
  hcpSignature: string;
  repSignature: string;
  hcpSignedBy?: string;
  hcpSignatureCapturedBy?: string;
  hcpSignatureDate?: string;
  hcpSignatureDateRaw?: string;
  repSignedBy?: string;
  repSignatureCapturedBy?: string;
  repSignatureDate?: string;
  repSignatureDateRaw?: string;
  hcpSignatureTerms?: string;
}

@Component({
  selector: 'medical-inquiry-dual-signature-modal',
  templateUrl: './medical-inquiry-dual-signature-modal.component.html',
  styleUrls: ['./medical-inquiry-dual-signature-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MedicalInquiryDualSignatureModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('hcpSignaturePad', { static: false }) hcpSignaturePad: SignaturePad;
  @ViewChild('repSignaturePad', { static: false }) repSignaturePad: SignaturePad;

  showHcpSignature = true;
  showRepSignature = true;
  hcpSignatureCaptured = false;
  repSignatureCaptured = false;
  hcpSignedBy = '';
  repSignatureCapturedBy = '';
  hcpSignatureDate: Date | null = null;
  repSignatureDate: Date | null = null;

  /** View-only mode: display captured signatures from existing MI (no pads, no submit) */
  viewOnly = false;
  hcpSignatureImage: string | null = null;
  repSignatureImage: string | null = null;

  hcpSignatureTerms = '';
  termBodyView: SafeHtml | null = null;
  isTermsViewLess = true;
  /**
   * True when the natural rendered terms content is so small that there is nothing meaningful
   * to collapse from (mirrors the `< 30 px` rule in consent-details / capture-allocation-order).
   * Only affects the View Less label state — View More is always enabled, and the button itself
   * is always visible whenever there is terms content.
   */
  isTermsViewLessDisabled = false;
  termsSectionHeaderViewData: IndSectionHeaderViewDataModel;

  viewOnlyHcpSignatureDate: string | null = null;
  viewOnlyRepSignatureDate: string | null = null;

  signaturePadOptions = {
    minWidth: 0.5,
    canvasWidth: 300,
    canvasHeight: 150,
  };

  @ViewChild('hcpSignatureCaptureArea', { static: false }) hcpSignatureCaptureArea: ElementRef<HTMLElement>;
  @ViewChild('repSignatureCaptureArea', { static: false }) repSignatureCaptureArea: ElementRef<HTMLElement>;

  hcpSectionHeaderViewData: IndSectionHeaderViewDataModel;
  repSectionHeaderViewData: IndSectionHeaderViewDataModel;

  private _resizeListener = () => this._onResizeOrOrientationChange();
  private _resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _observerDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly navParams: NavParams,
    public readonly translate: TranslateService,
    private readonly alertService: AlertService,
    private readonly authService: AuthenticationService,
    private readonly _cd: ChangeDetectorRef,
    private readonly datePipe: DatePipe,
    private readonly dateTimeFormats: DateTimeFormatsService,
    private readonly device: DeviceService,
    private readonly sanitizer: DomSanitizer
  ) {
    this.repSignatureCapturedBy =
      this.authService.user?.displayName ||
      [this.authService.user?.givenName, this.authService.user?.lastName].filter(Boolean).join(' ') ||
      '';
    this.hcpSignedBy = this.navParams.get('hcpContactName') || '';
    this.showHcpSignature = this.navParams.get('showHcpSignature') ?? false;
    this.showRepSignature = this.navParams.get('showRepSignature') ?? false;
    this.viewOnly = this.navParams.get('viewOnly') === true;
    if (this.viewOnly) {
      this.hcpSignatureImage = this.navParams.get('hcpSignature') || null;
      this.repSignatureImage = this.navParams.get('repSignature') || null;
      this.hcpSignatureCaptured = !!this.hcpSignatureImage;
      this.repSignatureCaptured = !!this.repSignatureImage;
      this.showHcpSignature = !!this.hcpSignatureImage;
      this.showRepSignature = !!this.repSignatureImage;
      this.viewOnlyHcpSignatureDate = this.navParams.get('viewOnlyHcpSignatureDate') || null;
      this.viewOnlyRepSignatureDate = this.navParams.get('viewOnlyRepSignatureDate') || null;
      // Reflect the original capturer (record owner), not whoever is viewing. When absent
      // (e.g. offline-created MI not yet synced), keep the logged-in user default set above —
      // for those records the viewer is the creator/owner, so it remains correct.
      const capturedBy = this.navParams.get('signatureCapturedBy') || '';
      if (capturedBy) this.repSignatureCapturedBy = capturedBy;
    }
    this.hcpSignatureTerms = this.navParams.get('hcpSignatureTerms') || '';
    this._updateTermsView();
    if (!this.device.isMobileDevicePortrait) {
      this.signaturePadOptions.canvasWidth = this._calculateWidth();
    }
  }

  private _calculateWidth(): number {
    return Math.floor(window.innerWidth * 0.8);
  }

  ngOnInit(): void {
    this._updateHcpSectionHeaderViewData();
    this._updateRepSectionHeaderViewData();
    this._updateTermsSectionHeaderViewData();
  }

  private _updateHcpSectionHeaderViewData(): void {
    this.hcpSectionHeaderViewData = {
      id: 'hcp-signature',
      title: this.translate.instant('HCP_SIGNATURE'),
      doNotModifyTitleCase: true,
      isRequired: true,
      controls: this.viewOnly ? [] : [{
        id: 'clear-hcp',
        text: this.translate.instant('CLEAR'),
        isDisabled: !this.hcpSignatureCaptured,
      }],
    };
  }

  private _updateRepSectionHeaderViewData(): void {
    this.repSectionHeaderViewData = {
      id: 'rep-signature',
      title: this.translate.instant('USER_SIGNATURE'),
      doNotModifyTitleCase: true,
      controls: this.viewOnly ? [] : [{
        id: 'clear-rep',
        text: this.translate.instant('CLEAR'),
        isDisabled: !this.repSignatureCaptured,
      }],
    };
  }

  private _updateTermsView(): void {
    if (this.hcpSignatureTerms) {
      this.termBodyView = this.sanitizer.bypassSecurityTrustHtml(`<p>${this.hcpSignatureTerms}</p>`);
    } else {
      this.termBodyView = null;
    }
    this._updateTermsSectionHeaderViewData();
  }

  private _updateTermsSectionHeaderViewData(): void {
    // Mirrors consent-details / capture-allocation-order convention:
    // - Button is always visible when there is terms content.
    // - "View More" is never disabled.
    // - "View Less" is disabled only when content is too small (isTermsViewLessDisabled).
    const isViewMoreShowing = this.isTermsViewLess;
    this.termsSectionHeaderViewData = {
      id: 'terms-header',
      title: this.translate.instant('TERMS_CAP'),
      controls: this.termBodyView ? [{
        id: 'view-expandable',
        text: isViewMoreShowing ? this.translate.instant('VIEW_MORE') : this.translate.instant('VIEW_LESS'),
        isVisible: true,
        isDisabled: !isViewMoreShowing && this.isTermsViewLessDisabled,
      }] : [],
    };
  }

  /**
   * Probe of the natural rendered terms content height.
   *
   * Reads the inner `<p>` rather than the container, because the container
   * carries `height: 24px; overflow: hidden` in collapsed state and its own
   * scrollHeight is floored at the padding-box height (so it can never read
   * less than the clamp). The `<p>` lays out at its natural size regardless
   * of the parent's clamp, giving a stable reading in both states.
   *
   * Run from ionViewDidEnter (modal fully presented), after viewport resize,
   * and after every toggle (so the disable-View-Less state stays correct).
   *
   * Fail-safe: if measurement returns 0 (called too early, layout not yet
   * applied, or element missing) we leave View Less enabled rather than
   * risk getting it stuck disabled.
   */
  private _measureTermsOverflow(): void {
    const container = document.getElementById('miTermBodyContainer');
    if (!container) return;
    const p = container.querySelector('p');
    const naturalHeight = p?.scrollHeight ?? 0;
    if (naturalHeight === 0) {
      this.isTermsViewLessDisabled = false;
    } else {
      // Disable View Less when content already fits within the .view-less
      // clamp (24 px) — collapsing would produce no visible change.
      this.isTermsViewLessDisabled = naturalHeight <= 24;
    }
    this._updateTermsSectionHeaderViewData();
    this._cd.markForCheck();
  }

  /** Pure boolean flip + re-probe so disable state stays correct after layout settles. */
  private _toggleTermsView(): void {
    this.isTermsViewLess = !this.isTermsViewLess;
    this._measureTermsOverflow();
  }

  get shouldShowTermsSection(): boolean {
    if (this.viewOnly) return !!this.hcpSignatureTerms;
    return !!this.termBodyView && this.showHcpSignature;
  }

  async onSectionHeaderControlClick(controlId: string): Promise<void> {
    if (controlId === 'clear-hcp') await this.clearHcpSignature();
    else if (controlId === 'clear-rep') await this.clearRepSignature();
    else if (controlId === 'view-expandable') this._toggleTermsView();
  }

  ngAfterViewInit(): void {
    if (!this.viewOnly) this._scheduleResize();
  }

  ionViewDidEnter(): void {
    if (!this.viewOnly) {
      this._scheduleResize();
      this._observeResize();
    }
    // Probe after the modal is fully presented — layout is guaranteed settled here.
    this._measureTermsOverflow();
    window.addEventListener('resize', this._resizeListener);
    window.addEventListener('orientationchange', this._resizeListener);
  }

  ionViewDidLeave(): void {
    this._stopObservingResize();
    window.removeEventListener('resize', this._resizeListener);
    window.removeEventListener('orientationchange', this._resizeListener);
    if (this._resizeDebounceTimer != null) {
      clearTimeout(this._resizeDebounceTimer);
      this._resizeDebounceTimer = null;
    }
  }

  ngOnDestroy(): void {
    this._stopObservingResize();
    window.removeEventListener('resize', this._resizeListener);
    window.removeEventListener('orientationchange', this._resizeListener);
    if (this._resizeDebounceTimer != null) {
      clearTimeout(this._resizeDebounceTimer);
    }
  }

  private _onResizeOrOrientationChange(): void {
    if (this._resizeDebounceTimer != null) {
      clearTimeout(this._resizeDebounceTimer);
    }
    this._resizeDebounceTimer = setTimeout(() => {
      this._resizeDebounceTimer = null;
      this._resizeCanvases(true);
      // Re-probe terms overflow after viewport change (content may reflow to different height).
      this._measureTermsOverflow();
    }, 150);
  }

  private _scheduleResize(): void {
    setTimeout(() => {
      const ok = this._resizeCanvases(false);
      if (!ok) setTimeout(() => this._resizeCanvases(true), 250);
    }, 150);
  }

  private _getSignatureContainer(): HTMLElement | null {
    return this.hcpSignatureCaptureArea?.nativeElement ?? this.repSignatureCaptureArea?.nativeElement ?? null;
  }

  private _observeResize(): void {
    const el = this._getSignatureContainer();
    if (!el || typeof ResizeObserver === 'undefined') return;
    this._resizeObserver = new ResizeObserver(() => {
      if (this._observerDebounceTimer != null) clearTimeout(this._observerDebounceTimer);
      this._observerDebounceTimer = setTimeout(() => {
        this._observerDebounceTimer = null;
        this._resizeCanvases(true);
      }, 100);
    });
    this._resizeObserver.observe(el);
  }

  private _stopObservingResize(): void {
    if (this._observerDebounceTimer != null) {
      clearTimeout(this._observerDebounceTimer);
      this._observerDebounceTimer = null;
    }
    const el = this._getSignatureContainer();
    if (this._resizeObserver && el) {
      this._resizeObserver.unobserve(el);
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  private _isPadReady(pad: SignaturePad | undefined): pad is SignaturePad {
    return !!(pad?.queryPad?.()?.canvas);
  }

  private _resizeCanvases(preserveSignatures: boolean): boolean {
    const container = this._getSignatureContainer();
    if (!container) return false;
    const img = container.querySelector('img');
    const imgWidth = img?.offsetWidth ?? 50;
    const padding = 40;
    const containerSpace = container.offsetWidth - imgWidth - padding;
    const calculatedWidth = this._calculateWidth();
    const canvasWidth = containerSpace > 100
      ? containerSpace
      : Math.min(calculatedWidth, 350);
    if (canvasWidth <= 50) return false;

    const hcpPadReady = this._isPadReady(this.hcpSignaturePad);
    const repPadReady = this._isPadReady(this.repSignaturePad);
    const hcpData = preserveSignatures && hcpPadReady && !this.hcpSignaturePad.isEmpty() ? this.hcpSignaturePad.toDataURL() : null;
    const repData = preserveSignatures && repPadReady && !this.repSignaturePad.isEmpty() ? this.repSignaturePad.toDataURL() : null;

    this.signaturePadOptions.canvasWidth = canvasWidth;
    if (hcpPadReady) {
      this.hcpSignaturePad.set('canvasWidth', canvasWidth);
      this.hcpSignaturePad.resizeCanvas();
    }
    if (repPadReady) {
      this.repSignaturePad.set('canvasWidth', canvasWidth);
      this.repSignaturePad.resizeCanvas();
    }

    if (hcpData && hcpPadReady) this.hcpSignaturePad.fromDataURL(hcpData);
    if (repData && repPadReady) this.repSignaturePad.fromDataURL(repData);

    this._cd.markForCheck();
    // Return false to trigger retry when pads that need resizing are not yet ready
    const padsNeedRetry = (this.showHcpSignature && !hcpPadReady) || (this.showRepSignature && !repPadReady);
    return !padsNeedRetry;
  }

  /**
   * Submit rule:
   * - If HCP section is visible, HCP signature is required.
   * - If only user/rep section is visible, submit is always allowed.
   */
  get canSubmit(): boolean {
    if (this.showHcpSignature) return this.hcpSignatureCaptured;
    if (this.showRepSignature) return true;
    return false;
  }

  get shouldShowRepSection(): boolean {
    if (this.viewOnly) return !!this.repSignatureImage;
    return this.showRepSignature;
  }

  get shouldShowHcpSection(): boolean {
    if (this.viewOnly) return !!this.hcpSignatureImage;
    return this.showHcpSignature;
  }

  onHcpDrawStart(): void {
    this._cd.markForCheck();
  }

  onHcpDrawEnd(): void {
    this.hcpSignatureCaptured = !this.hcpSignaturePad?.isEmpty();
    this.hcpSignatureDate = new Date();
    this._updateHcpSectionHeaderViewData();
    this._cd.markForCheck();
  }

  onRepDrawStart(): void {
    this._cd.markForCheck();
  }

  onRepDrawEnd(): void {
    this.repSignatureCaptured = !this.repSignaturePad?.isEmpty();
    this.repSignatureDate = new Date();
    this._updateRepSectionHeaderViewData();
    this._cd.markForCheck();
  }

  async clearHcpSignature(): Promise<void> {
    const result = await this.alertService.showAlert(
      {
        title: this.translate.instant('CLEAR_HCP_SIGNATURE'),
        message: this.translate.instant('CLEAR_HCP_SIGNATURE_MESSAGE'),
      },
      this.translate.instant('CLEAR'),
      this.translate.instant('CANCEL')
    );
    if (result?.role !== 'ok') return;
    this.hcpSignaturePad?.clear();
    this.hcpSignatureCaptured = false;
    this.hcpSignatureDate = null;
    this._updateHcpSectionHeaderViewData();
    this._cd.markForCheck();
  }

  async clearRepSignature(): Promise<void> {
    const result = await this.alertService.showAlert(
      {
        title: this.translate.instant('CLEAR_USER_SIGNATURE'),
        message: this.translate.instant('CLEAR_USER_SIGNATURE_MESSAGE'),
      },
      this.translate.instant('CLEAR'),
      this.translate.instant('CANCEL')
    );
    if (result?.role !== 'ok') return;
    this.repSignaturePad?.clear();
    this.repSignatureCaptured = false;
    this.repSignatureDate = null;
    this._updateRepSectionHeaderViewData();
    this._cd.markForCheck();
  }

  async cancel(): Promise<void> {
    if (
      !this.viewOnly &&
      (this.hcpSignatureCaptured || this.repSignatureCaptured)
    ) {
      const result = await this.alertService.showAlert(
        {
          title: this.translate.instant('DISCARD_SIGNATURES'),
          message: this.translate.instant('DISCARD_SIGNATURES_MESSAGE'),
        },
        this.translate.instant('CANCEL_AND_DISCARD'),
        this.translate.instant('STAY')
      );
      if (result?.role !== 'ok') return;
    }
    this.modalCtrl.dismiss(null);
  }

  submit(): void {
    if (!this.canSubmit) return;
    const result: MedicalInquiryDualSignatureResult = {
      hcpSignature: this.showHcpSignature && this.hcpSignatureCaptured ? this.hcpSignaturePad?.toDataURL() ?? '' : '',
      repSignature: this.showRepSignature && this.repSignatureCaptured ? this.repSignaturePad?.toDataURL() ?? '' : '',
      hcpSignedBy: this.hcpSignedBy,
      hcpSignatureCapturedBy: this.repSignatureCapturedBy,
      hcpSignatureDate: this._formatSignatureDate(this.hcpSignatureDate),
      hcpSignatureDateRaw: this.hcpSignatureDate?.toISOString(),
      repSignedBy: this.repSignatureCapturedBy,
      repSignatureCapturedBy: this.repSignatureCapturedBy,
      repSignatureDate: this._formatSignatureDate(this.repSignatureDate),
      repSignatureDateRaw: this.repSignatureDate?.toISOString(),
      hcpSignatureTerms: this.hcpSignatureTerms || undefined,
    };
    this.modalCtrl.dismiss(result);
  }

  private _formatSignatureDate(date: Date | null): string | undefined {
    if (!date) return undefined;
    return this.datePipe.transform(date, this.dateTimeFormats.dateTime, undefined, this.translate.currentLang) ?? undefined;
  }
}
